import { toEtDateString } from '@low6-pickem/sports-config';
import type { DaySummary } from '@/types/reveal';

/**
 * Split picked days into the top "Upcoming Game" list (not yet graded) and the
 * graded history list under "My Weekly Selections".
 *
 * Graded is keyed on `userEntry.status`, not day.status / fixture finished —
 * finished-but-ungraded picks stay on top until scoring lands.
 */
export function splitUpcomingAndRecentDays(days: DaySummary[]): {
  upcomingDays: DaySummary[];
  recentDays: DaySummary[];
} {
  const withPick = days.filter(d => d.userEntry?.selectedCard != null);
  const upcomingDays = withPick
    .filter(d => d.userEntry?.status !== 'graded')
    .sort((a, b) => a.dayNumber - b.dayNumber);
  const recentDays = withPick
    .filter(d => d.userEntry?.status === 'graded')
    .sort((a, b) => b.dayNumber - a.dayNumber);
  return { upcomingDays, recentDays };
}

/**
 * Screenshot-style date label above each upcoming pick card.
 * Uses ET calendar dates so NFL Tue/Wed packs label correctly overnight.
 */
export function formatUpcomingPickDateLabel(
  gameDate: string,
  nowMs: number = Date.now(),
  categoryName?: string | null
): string {
  const formatted = formatGameDateLabel(gameDate);
  const todayEt = toEtDateString(new Date(nowMs));
  const base = gameDate === todayEt ? `Today - ${formatted}` : formatted;
  return categoryName ? `${categoryName} · ${base}` : base;
}

function formatGameDateLabel(gameDate: string): string {
  const d = new Date(`${gameDate}T12:00:00`);
  // Screenshot style: "Wed Sep 16" (no comma), independent of locale defaults.
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' });
  const month = d.toLocaleDateString('en-US', { month: 'short' });
  const day = d.toLocaleDateString('en-US', { day: 'numeric' });
  return `${weekday} ${month} ${day}`;
}
